import { FormSectionHandler } from '../entity.handler';
import { AssetResponse, AssetUpdateRequest, IdName } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils } from '../utils';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';


interface SectionValues {
    sharedWith: ListFieldValue<IdName>[];
}

export class AssetSharingSectionHandler implements FormSectionHandler<AssetResponse, SectionValues> {

    type = 'form';
    icon = 'share-alt';
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');
    invalidPin: boolean = false;

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ASSET.SHARING.${key}`, values);
    }

    applyUpdate(entity: AssetResponse, payload: AssetUpdateRequest): Promise<AssetResponse> {
        return this.admin.applyUpdate(api => api.entities.updateAsset(entity.id, payload));
    }

    mapEntityToEditor(entity: AssetResponse): Promise<SectionValues> {
        return Promise.resolve({ sharedWith: (entity.sharedWith || []).map(x => this.idNameToListItem(x)) });
    }

    mapEditorToEntity(entity: AssetUpdateRequest, values: SectionValues): Promise<AssetUpdateRequest> {
        entity.sharedWith = values.sharedWith.map(x => x.data);
        return Promise.resolve(entity);
    }

    idNameToListItem(item: IdName): ListFieldValue<IdName> {
        return {
            id: item.id,
            name: item.name,
            icon: 'home',
            data: item,
        };
    }

    getFields(entity: AssetResponse): Promise<FormBuilderField[]> {
        return Promise.resolve([
            {
                id: 'sharedWith', title: this.translate('FIELDS.SHARED_WITH'), type: 'list', required: false,
                actions: [
                    { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showAddCompanyModal.bind(this), },
                ],
                getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
            },
        ]).then(fields => EntityUtils.prepareBulk(!entity, ['sharedWith'], fields));
    }

    showAddCompanyModal(_action: string, _field: FormBuilderField, values: SectionValues): Promise<IdName> {

        return this.admin.showFormModal({
            groups: [{
                name: this.translate('MODAL.TITLE'),
                description: this.translate('MODAL.DESC'),
                fields: [
                    {
                        id: 'pin', title: this.translate('FIELDS.PIN'), type: 'text', min: 1, max: 20, required: true,
                        onChange: async (_field, values) => {
                            const pin: string = values['pin'].replace('-', '').trim();
                            const client = await this.admin.getCached(`getClientByPin/${pin}`, api => api.accounts.getClientByPin(pin), 1).catch(_err => {
                                return null;
                            });
                            this.invalidPin = !client;
                        },
                        validate: (_field, _values, _value) => {
                            if (this.invalidPin) {
                                return this.translate('MODAL.INVALID_PIN_MESSAGE');
                            }
                        }
                    },
                ],
            }],
        }, { pin: '' }
        ).then(result => {
            if (result) {
                result.pin = result.pin.replace('-', '').trim();
                return this.admin.getCached(`getClientByPin/${result.pin}`, api => api.accounts.getClientByPin(result.pin), 1).then(client => {
                    const existing = values.sharedWith.find(x => x.id === client.id);
                    if (!existing) {
                        values.sharedWith.push(this.idNameToListItem(client));
                    }
                    return client;
                });
            }
            return null;
        });


    }

}
